import { Service, computed, inject, resource } from '@angular/core';

import { JerarquiaClientesStore } from '@compartido/clientes/jerarquia-clientes.store';
import { recargarCada } from '@core/datos/polleado';
import { PermisosService } from '@core/permisos/permisos.service';

import { alcanceEventos, consultaAbiertos, tiposEventoVisibles } from './alcance-eventos';
import { AvisoEventosNuevosStore } from './aviso-eventos-nuevos.store';
import { EventosHttp } from './entidades.http';

/** Cada cuánto se recuenta. Es un número en el menú, no una vista. */
const CADENCIA_SEG = 60;

/**
 * Cuántos eventos de alumbrado están abiertos.
 *
 * Es el badge del menú: el operador tiene que ver que hay algo **sin entrar** a
 * la vista. Vive acá y no en el componente de eventos porque lo pide el shell,
 * que se monta antes y no puede depender de un dominio.
 *
 * **Pide el conteo, no los eventos.** `onlyTotalCount` trae sólo el total, sin
 * documentos; traer los 2.000 para hacerles `.length` es el error que convierte
 * un badge en medio segundo de espera cada minuto.
 */
@Service()
export class EventosAbiertosStore {
  private readonly http = inject(EventosHttp);
  private readonly permisos = inject(PermisosService);
  private readonly jerarquia = inject(JerarquiaClientesStore);

  constructor() {
    // El aviso sonoro vive mientras vive el badge: los dos son "hay algo abierto
    // sin entrar a la vista", y el shell sólo se monta con la sesión iniciada.
    inject(AvisoEventosNuevosStore);

    // Con `reload()`: un contador en los params dejaba el conteo en `undefined`
    // en cada vuelta y el badge se apagaba y volvía a prender.
    recargarCada([this.conteo], CADENCIA_SEG);
  }

  private readonly conteo = resource({
    // Sin permiso para ver eventos no se cuenta: el ítem ni está en el menú.
    // Con hijos se cuentan también los suyos; sin hijos, `includeChildren` sólo
    // haría lenta la consulta (ver `JerarquiaClientesStore.tieneHijos`).
    params: () => {
      const hijos = this.jerarquia.tieneHijos();
      if (!this.permisos.puedeVer('eventos') || hijos === undefined) return undefined;
      return { tipos: tiposEventoVisibles(this.permisos), hijos };
    },
    loader: ({ params }) =>
      this.http.listar(
        {
          ...consultaAbiertos(alcanceEventos(params.tipos), { hijos: true }),
          onlyTotalCount: true,
          includeChildren: params.hijos || undefined,
        },
        // Es un recuento de fondo cada minuto: si una vuelta falla, la próxima
        // lo corrige. Un aviso de error en medio de cualquier pantalla no le
        // sirve a nadie (el badge queda en lo último que llegó).
        { silencioso: true },
      ),
  });

  /**
   * `0` también cuando falla: un badge que miente para arriba es peor que ninguno.
   * `hasValue()` antes de `value()`: en error, `value()` tira `ResourceValueError`
   * y rompe la detección de cambios de la pantalla entera, no sólo el badge.
   */
  readonly abiertos = computed(() =>
    this.conteo.hasValue() ? (this.conteo.value().totalCount ?? 0) : 0,
  );
}
